import { Stack } from '@mui/material'
import { CarrierVisitDirection, OrderStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { NoMatchingAllocationRuleAlert } from '@planning/pages/GateControl/Atoms/NoMatchingAllocationRuleAlert'
import { NoSpaceInExistingAllocationAlert } from '@planning/pages/GateControl/Atoms/NoSpaceInExistingAllocationAlert'
import { CustomAlert } from '@planning/pages/TallymanV2/Components'
import { HoldValidationContext } from '@planning/utils/hold-enforcement'
import { isValidityExpiredByNow } from '@planning/utils/release-validity-utils'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { HoldEnforcementAlertWrapper } from '../hold/HoldEnforcementAlertWrapper'

interface Props {
  status: OrderStatus
  order?: any
  context?: HoldValidationContext
  isOnTerminal?: boolean
  direction?: CarrierVisitDirection
  releaseEnd?: string | null
  plannedYardLocation?: string | null
  invalidAllocationSpace?: boolean
  incorrectAssignedCraneName?: string | null
  noPlannedEquipment?: boolean
  isApproved?: boolean
  noMargin?: boolean
  hideHoldEnforcement?: boolean
}

export const OrderAlerts = observer(
  ({
    status,
    order,
    context = 'GateIn',
    isOnTerminal,
    direction,
    releaseEnd,
    plannedYardLocation,
    invalidAllocationSpace,
    incorrectAssignedCraneName,
    noPlannedEquipment,
    isApproved,
    noMargin,
    hideHoldEnforcement,
  }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { tenantStore } = usePlanningStore()
    const isReleaseExpired = isValidityExpiredByNow(releaseEnd)

    return (
      <Stack gap={theme.customSpacing.m} mt={noMargin ? 0 : theme.customSpacing.m}>
        {direction === CarrierVisitDirection.Inbound && isApproved === false && (
          <CustomAlert
            message={t(
              'dischargeApprovalIsMissing',
              'Discharge approval is missing. Contact control room',
            )}
            severity='warning'
          />
        )}
        {direction === CarrierVisitDirection.Outbound && isApproved === false && (
          <CustomAlert
            message={t(
              'loadingApprovalIsMissing',
              'Loading approval is missing. Contact control room',
            )}
            severity='warning'
          />
        )}

        {isOnTerminal !== undefined && isOnTerminal && direction === 'Inbound' && (
          <CustomAlert
            message={t(
              'containerIsAlreadyOnTerminalContactControlRoom',
              'Container is already on terminal, contact control room',
            )}
            severity='warning'
          />
        )}

        {status === OrderStatus.Open &&
          isOnTerminal !== undefined &&
          !isOnTerminal &&
          direction === 'Outbound' && (
            <CustomAlert
              message={
                tenantStore.allowGateInWhenUnitNotOnTerminal
                  ? t(
                      'unitNotOnTerminalYetButTrucksArePermittedToProceed',
                      'Unit (container or trailer) not on terminal yet, but trucks are permitted to proceed',
                    )
                  : t('unitNotOnTerminalYet', 'Unit not on terminal yet')
              }
              severity='warning'
            />
          )}

        {!hideHoldEnforcement && (
          <HoldEnforcementAlertWrapper order={order} context={context} direction={direction} />
        )}

        {isReleaseExpired && !tenantStore.skipReleaseOrder && (
          <CustomAlert
            message={t(
              'containerReleaseHasExpiredContactControlRoom',
              'Container release has expired, contact control room',
            )}
            severity='warning'
          />
        )}

        {!!incorrectAssignedCraneName && (
          <CustomAlert
            message={t('theContainerIsPlannedForCrane', 'The container is planned for {crane}', {
              crane: incorrectAssignedCraneName,
            })}
            severity='warning'
          />
        )}

        {noPlannedEquipment && direction === CarrierVisitDirection.Inbound && (
          <CustomAlert
            message={t(
              'noEquipmentAssignedContactControlRoom',
              'No equipment assigned. Contact control room',
            )}
            severity='warning'
          />
        )}

        {direction === 'Inbound' && invalidAllocationSpace && <NoSpaceInExistingAllocationAlert />}

        {direction === 'Inbound' && !plannedYardLocation && <NoMatchingAllocationRuleAlert />}
      </Stack>
    )
  },
)
